const ROMAN_VALUES: Record<string, number> = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 };

function romanToInt(token: string): number | null {
   let total = 0;
   for (let index = 0; index < token.length; index++) {
      const current = ROMAN_VALUES[token[index]];
      const next = ROMAN_VALUES[token[index + 1]] ?? 0;
      if (!current) return null;
      total += current < next ? -current : current;
   }
   return total > 0 ? total : null;
}

function intToRoman(value: number): string {
   const numerals: Array<[number, string]> = [
      [50, "L"], [40, "XL"], [10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"],
   ];
   let rest = value;
   let roman = "";
   for (const [amount, glyph] of numerals) {
      while (rest >= amount) {
         roman += glyph;
         rest -= amount;
      }
   }
   return roman;
}

const ROMAN_WORDS = new Set(["vi", "di", "mid", "lid", "dim", "mix", "did", "mil", "liv", "ill", "civ", "vim"]);

function romanToken(token: string): number | null {
   const upper = token.toUpperCase();
   if (token !== upper && token !== token.toLowerCase()) return null;
   if (token.length === 1 && token !== upper) return null;
   if (token === token.toLowerCase() && ROMAN_WORDS.has(token.toLowerCase())) return null;
   return strictRoman(upper);
}

function strictRoman(token: string): number | null {
   if (!/^[IVXLCDM]+$/.test(token)) return null;
   const value = romanToInt(token);
   if (!value || value > 50) return null;
   return intToRoman(value) === token ? value : null;
}

function nearly(left: number, right: number): boolean {
   return Number.isFinite(left) && Number.isFinite(right) && Math.abs(left - right) < 0.001;
}

function covers(ranges: Array<[number, number]>, index: number): boolean {
   return ranges.some(([start, end]) => index >= start && index < end);
}

/** Place identifiers in a document phrase or an element name. III and 3 are the same key. XI is 11, not 3. */
export function placeKeys(text: string, ignoreValue?: number | null): Set<string> {
   const keys = new Set<string>();
   if (!text) return keys;
   const stripped = text
      .replace(/§\s*\d+[a-z]?/gi, " ")
      .replace(/\bstk\.?\s*\d+/gi, " ");
   const occupied: Array<[number, number]> = [];

   for (const match of stripped.matchAll(/\b\d{1,4}(?:\.\d{1,4})+\b/g)) {
      const raw = match[0];
      const index = match.index ?? 0;
      occupied.push([index, index + raw.length]);
      if (/^\d{1,3}(?:\.\d{3})+$/.test(raw)) continue;
      if (ignoreValue != null && nearly(Number(raw), ignoreValue)) continue;
      keys.add(raw);
   }

   for (const match of stripped.matchAll(/\b([A-Za-z]+)\s*[-–]\s*([A-Za-z]+)\b/g)) {
      const start = romanToken(match[1]);
      const end = romanToken(match[2]);
      if (!start || !end || end < start || end - start > 20) continue;
      for (let value = start; value <= end; value++) keys.add(String(value));
      occupied.push([match.index ?? 0, (match.index ?? 0) + match[0].length]);
   }

   for (const match of stripped.matchAll(/\b[A-Za-z]+\b/g)) {
      if (covers(occupied, match.index ?? 0)) continue;
      const value = romanToken(match[0]);
      if (value) keys.add(String(value));
   }

   for (const match of stripped.matchAll(/\b\d{1,3}\b/g)) {
      if (covers(occupied, match.index ?? 0)) continue;
      const value = Number(match[0]);
      if (ignoreValue != null && nearly(value, ignoreValue)) continue;
      keys.add(String(value));
   }

   return keys;
}

export function samePlace(documentText: string, elementName: string, value: number | null): boolean {
   const documentKeys = placeKeys(documentText, value);
   const elementKeys = placeKeys(elementName);
   if (!documentKeys.size || !elementKeys.size) return false;
   for (const key of elementKeys) {
      if (documentKeys.has(key)) return true;
   }
   return false;
}
